import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { collection, query, getDocs } from "firebase/firestore";
import { db } from "../utils/firebase.js";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { Spinner } from "../components/index.js";
import { useAuth } from "../hooks/AuthenticationProvider";

const CustomTooltip = ({
  active,
  payload,
  label,
  dataKeys,
  formatSobriety,
  formatStayDuration,
}) => {
  // Helper function to check if a value is valid for numeric operations
  const isValidNumber = (val) => {
    return val !== null && val !== undefined && !isNaN(val) && val !== "";
  };

  if (active && payload && payload.length) {
    const note = payload[0]?.payload?.note || null;

    const data = dataKeys
      .map((key) => {
        const matched = payload.find(
          (p) => p.dataKey === key.dataKey || p.name === key.label
        );
        // Skip entries with zero value for sobriety time
        if (matched && key.label.includes("Sobriety")) {
          if (matched.value === 0) {
            return null;
          }
          // Handle the value formatting here
          return {
            label: key.label,
            value: isValidNumber(matched.value)
              ? formatSobriety(matched.value)
              : "N/A",
          };
        }

        if (matched) {
          // Start with the raw value and add safety checks
          let value = matched.value;

          if (key.label.includes("Stay") || key.label.includes("Sober Days")) {
            // Handle stay duration formatting with safety check
            value = isValidNumber(value) ? formatStayDuration(value) : "N/A";
          } else if (
            [
              "Healthy Transition",
              "Removed",
              "Against Advice",
              "On MAT",
              "Not On MAT",
              "On Probation/Parole",
              "Not On Probation/Parole",
              "%",
            ].includes(key.label)
          ) {
            // Handle percentage formatting with safety check
            value = isValidNumber(value)
              ? `${parseFloat(value).toFixed(1)}%`
              : "N/A";
          } else {
            // Handle all other cases with safety check
            value = isValidNumber(value) ? value : "N/A";
          }

          return {
            label: key.label,
            value,
          };
        }
        return null;
      })
      .filter(
        (item) =>
          item !== null &&
          (!item.label?.includes("Sobriety") || item.value !== "0m")
      ); // Filter out null items and zero sobriety values

    return (
      <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 text-slate-300">
        <p className="text-purple-400 font-semibold mb-2">{`Timestamp: ${label}`}</p>
        <div className="space-y-1">
          {data.map((item) => (
            <p key={item.label} className="text-slate-300">
              <span className="font-medium text-purple-400">{item.label}:</span>{" "}
              {item.value}
            </p>
          ))}
        </div>
        {note && (
          <p className="mt-3 text-sm italic text-amber-400">{`Note: ${note}`}</p>
        )}
      </div>
    );
  }
  return null;
};

const DataTracker = () => {
  const { user, isLoading, organizationId, organizationData } = useAuth();

  const navigate = useNavigate();
  const housesData = organizationData?.housesData || {};

  const [chartFilter, setChartFilter] = useState("ageDemographics");
  const [chartData, setChartData] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const colors = ["#8884d8", "#82ca9d", "#ffc658", "#f58231", "#A020F0"];
  const getDynamicColor = (index) => colors[index % colors.length];

  const formatSobriety = (months) => {
    if (isNaN(months) || months <= 0) return "0m";
    const years = Math.floor(months / 12);
    const remainingMonths = Math.round(months % 12);
    return `${years}y ${remainingMonths}m`;
  };

  const formatStayDuration = (value) => {
    if (isNaN(value) || value === null) return "N/A";
    const months = Math.floor(value / 30);
    const days = Math.round(value % 30);
    return `${months} months, ${days} days`;
  };

  const convertDaysToMonths = (days) => {
    return days / 30;
  };

  const calculateSobrietyMonths = (snapshot, houseName) => {
    // Try the new format (sobrietyTimes map)
    if (snapshot?.sobrietyTimes?.[houseName]) {
      const sobrietyData = snapshot.sobrietyTimes[houseName];
      const years = parseInt(sobrietyData?.years || 0, 10);
      const months = parseInt(sobrietyData?.months || 0, 10);
      const days = parseInt(sobrietyData?.days || 0, 10);
      return years * 12 + months + days / 30;
    }

    // Try the old format (direct houseSobriety map)
    const oldFormatKey = `${houseName.toLowerCase()}Sobriety`;
    if (snapshot?.[oldFormatKey]) {
      const oldFormatData = snapshot[oldFormatKey];
      const years = parseInt(oldFormatData?.years || 0, 10);
      const months = parseInt(oldFormatData?.months || 0, 10);
      const days = parseInt(oldFormatData?.days || 0, 10);
      return years * 12 + months + days / 30;
    }

    return 0; // Return 0 if neither format is found
  };

  useEffect(() => {
    const fetchWeeklySnapshots = async () => {
      if (!organizationId) {
        console.error(
          "No organizationId provided. Skipping fetch for weeklySnapshots."
        );
        setChartData([]);
        return;
      }

      try {
        const snapshotsRef = collection(
          db,
          `organizations/${organizationId}/weeklySnapshots`
        );
        const querySnapshot = await getDocs(snapshotsRef);

        if (querySnapshot.empty) {
          console.warn("No weeklySnapshots found for this organization.");
          setChartData([]);
          return;
        }

        // Rest of your snapshot processing logic remains the same...
        const newData = querySnapshot.docs
          .filter((doc) => doc.id !== "initial")

          .map((doc) => {
            const data = doc.data();
            let formattedDate = "Unknown Date";

            // Check if timestamp exists and is valid
            if (data.timestamp && data.timestamp.toDate) {
              const timestampDate = data.timestamp.toDate();
              formattedDate = new Intl.DateTimeFormat("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              }).format(timestampDate);

              return { ...data, timestamp: timestampDate, formattedDate }; // Include raw timestamp for sorting
            } else {
              console.warn("Missing or invalid timestamp in snapshot:", data);
              return null; // Exclude invalid entries
            }
          })
          .filter((item) => item !== null) // Remove any invalid entries
          .sort((a, b) => a.timestamp - b.timestamp) // Sort by timestamp (oldest to newest)
          .map(({ formattedDate, ...data }) => {
            let chartDataItem = {
              timestamp: formattedDate,
              percentageFull: data.percentageFull || 0,
              note: data.note || null,
            };

            switch (chartFilter) {
              case "ageDemographics":
                const demographics = [
                  "18-25",
                  "26-35",
                  "36-45",
                  "46-55",
                  "56-65+",
                ];
                const demographicsData = demographics.reduce(
                  (acc, ageGroup) => {
                    const value = data.ageDemographics?.[ageGroup] || 0;
                    acc[ageGroup] = value;
                    return acc;
                  },
                  {}
                );

                chartDataItem = {
                  ...chartDataItem,
                  ...demographicsData,
                };
                break;

              case "averageLengthOfStay":
                const stayDataByHouse = Object.entries(housesData).reduce(
                  (acc, [houseName]) => {
                    const stayValue = data?.averageLengthOfStay?.[houseName];
                    // Only add the value if it's greater than 0
                    if (stayValue > 0) {
                      acc[`${houseName} Average Stay`] = stayValue;
                    }
                    return acc;
                  },
                  {}
                );

                chartDataItem = {
                  ...chartDataItem,
                  ...stayDataByHouse,
                  "Total Average Stay":
                    data?.averageLengthOfStay?.total > 0
                      ? data.averageLengthOfStay.total
                      : null,
                };
                break;

              case "employmentData":
                // Check for both data formats (new and old)
                if (data.employmentPercentages) {
                  // New format with separate percentages
                  chartDataItem = {
                    ...chartDataItem,
                    "Employed at Intake":
                      data.employmentPercentages.intake || 0,
                    "Employed at Discharge":
                      data.employmentPercentages.discharge || 0, // Changed from 'current' to 'discharge'
                  };
                } else if (data.employmentData) {
                  // Calculate percentages manually if only raw data is available
                  const intakeTotal =
                    (data.employmentData.intake?.employed || 0) +
                    (data.employmentData.intake?.unemployed || 0);

                  const dischargeTotal =
                    (data.employmentData.discharge?.employed || 0) + // Changed from 'current' to 'discharge'
                    (data.employmentData.discharge?.unemployed || 0); // Changed from 'current' to 'discharge'

                  chartDataItem = {
                    ...chartDataItem,
                    "Employed at Intake":
                      intakeTotal > 0
                        ? (data.employmentData.intake.employed / intakeTotal) *
                          100
                        : 0,
                    "Employed at Discharge":
                      dischargeTotal > 0
                        ? (data.employmentData.discharge.employed / // Changed from 'current' to 'discharge'
                            dischargeTotal) *
                          100
                        : 0,
                  };
                }
                break;

              case "soberDaysOnIntake":
                const soberDaysDataByHouse = Object.entries(housesData).reduce(
                  (acc, [houseName]) => {
                    const soberDays =
                      data?.averageSoberDaysOnIntake?.[houseName];
                    if (soberDays > 0) {
                      acc[`${houseName} Sober Days`] = soberDays;
                    }
                    return acc;
                  },
                  {}
                );

                chartDataItem = {
                  ...chartDataItem,
                  ...soberDaysDataByHouse,
                  "Total Sober Days":
                    data?.averageSoberDaysOnIntake?.total > 0
                      ? data.averageSoberDaysOnIntake.total
                      : null,
                };
                break;

              case "medications":
                chartDataItem = {
                  ...chartDataItem,
                  ...data.medicationData.reduce((acc, medication, index) => {
                    acc[`${index + 1} Medication${index === 0 ? "" : "s"}`] =
                      medication?.count || 0;
                    return acc;
                  }, {}),
                  "5+ Medications": data.medicationData
                    .slice(5)
                    .reduce((total, med) => total + (med?.count || 0), 0),
                };
                break;

              case "probationChart":
                const probationTotal = data?.probationParoleData?.reduce(
                  (acc, item) => acc + (item?.value || 0),
                  0
                );

                chartDataItem = {
                  ...chartDataItem,
                  "On Probation/Parole": probationTotal
                    ? (data.probationParoleData[0]?.value / probationTotal) *
                      100
                    : 0,
                  "Not On Probation/Parole": probationTotal
                    ? (data.probationParoleData[1]?.value / probationTotal) *
                      100
                    : 0,
                };
                break;
              case "recidivismData":
                // Handle both filter types from snapshot
                const allRecidivismData = data?.recidivismData?.all || [];
                const probationRecidivismData =
                  data?.recidivismData?.probationParole || []; // Also fix the typo: probrationParole -> probationParole

                // Calculate percentages for All Alumni
                const allTotal = allRecidivismData.reduce(
                  (sum, item) => sum + (item?.value || 0),
                  0
                );
                const allRearrested = allRecidivismData.find(
                  (item) => item.name === "Re-arrested"
                );

                // Calculate percentages for Probation/Parole - renamed variable to avoid conflict
                const recidivismProbationTotal = probationRecidivismData.reduce(
                  (sum, item) => sum + (item?.value || 0),
                  0
                );
                const probationRearrested = probationRecidivismData.find(
                  (item) => item.name === "Re-arrested"
                );

                chartDataItem = {
                  ...chartDataItem,
                  "All Alumni Recidivism":
                    allTotal > 0 && allRearrested
                      ? parseFloat(allRearrested.percentage || 0)
                      : 0,
                  "Probation/Parole Recidivism":
                    recidivismProbationTotal > 0 && probationRearrested
                      ? parseFloat(probationRearrested.percentage || 0)
                      : 0,
                };
                break;
              case "dischargeStatus":
                const findStatusPercentage = (statusName) => {
                  const entry = data.dischargeStatusData.find(
                    (item) =>
                      item.name.replace(/\s+/g, "").toLowerCase() ===
                      statusName.replace(/\s+/g, "").toLowerCase()
                  );
                  return parseFloat(entry?.percentage || 0);
                };

                chartDataItem = {
                  ...chartDataItem,
                  "Healthy Transition":
                    findStatusPercentage("HealthyTransition"),
                  Removed: findStatusPercentage("Removed"),
                  "Against Advice": findStatusPercentage("AgainstAdvice"),
                };
                break;

              case "matData":
                const totalMatCount = data?.matData?.reduce(
                  (acc, item) => acc + (item?.value || 0),
                  0
                );

                chartDataItem = {
                  ...chartDataItem,
                  "On MAT": totalMatCount
                    ? (data.matData[0]?.value / totalMatCount) * 100
                    : 0,
                  "Not On MAT": totalMatCount
                    ? (data.matData[1]?.value / totalMatCount) * 100
                    : 0,
                };
                break;
              case "sobrietyTime":
                const sobrietyDataByHouse = Object.entries(housesData).reduce(
                  (acc, [houseName]) => {
                    const months = calculateSobrietyMonths(data, houseName);
                    if (months > 0) {
                      acc[`${houseName} Sobriety`] = months;
                    }
                    return acc;
                  },
                  {}
                );

                const totalMonths = Object.values(sobrietyDataByHouse).reduce(
                  (sum, months) => sum + months,
                  0
                );

                chartDataItem = {
                  ...chartDataItem,
                  ...sobrietyDataByHouse,
                  "Total Sobriety": totalMonths > 0 ? totalMonths : null,
                };
                break;

              case "timeSinceLastRelapse":
                chartDataItem = {
                  ...chartDataItem,
                  "Time Since Last Relapse": convertDaysToMonths(
                    data?.timeSinceLastRelapse
                  ),
                };
                break;

              case "percentageFull":
                chartDataItem = {
                  ...chartDataItem,
                  "Percentage Full": data?.percentageFull || 0,
                };
                break;

              default:
                break;
            }

            return chartDataItem;
          });

        setChartData(newData);
      } catch (error) {
        console.error("Error fetching weekly snapshots:", error);
        setChartData([]);
      }
    };

    fetchWeeklySnapshots();
  }, [organizationId, chartFilter, housesData]);

  const handleChartFilterChange = (event) => {
    setChartFilter(event.target.value);
  };

  function formatPercentage(value) {
    return `${value <= 1 ? value * 100 : value}%`;
  }

  function shouldDisplayPercentages() {
    return (
      chartFilter === "matData" ||
      chartFilter === "percentageFull" ||
      chartFilter === "probationChart" ||
      chartFilter === "dischargeStatus" ||
      chartFilter === "employmentData" ||
      chartFilter === "recidivismData"
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <>
      <Helmet>
        <title>&bull; Analytics Tracker</title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
        {/* Ambient background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto space-y-8 relative">
          {/* Header */}
          <div className="flex items-center gap-6 mb-12">
            <div className="flex gap-3">
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">
                Analytics Tracker
              </h1>
            </div>
          </div>

          <div className="bg-slate-900/50 backdrop-blur-xl rounded-lg sm:rounded-xl overflow-hidden border border-slate-800/50">
            <div className="p-3 sm:p-6">
              <div className="relative w-full sm:inline-block sm:w-auto">
                <select
                  value={chartFilter}
                  onChange={handleChartFilterChange}
                  className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base text-slate-300 bg-slate-900 border border-slate-700 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200"
                >
                  <option value="ageDemographics">Age Demographics</option>
                  <option value="sobrietyTime">Sobriety Time</option>
                  <option value="soberDaysOnIntake">
                    Sober Days on Intake
                  </option>
                  <option value="employmentData">Employment Data</option>
                  <option value="medications">Medications</option>
                  <option value="probationChart">Probation Chart</option>
                  <option value="dischargeStatus">Discharge Status</option>
                  <option value="matData">MAT Data</option>
                  <option value="recidivismData">Recidivism Data</option>
                  <option value="percentageFull">Percent Full</option>
                  <option value="averageLengthOfStay">
                    Average Length of Stay
                  </option>
                </select>
              </div>

              <div className="mt-4 sm:mt-6 bg-slate-800/30 rounded-lg sm:rounded-xl p-2 sm:p-4">
                <ResponsiveContainer
                  width="100%"
                  height={window.innerWidth < 768 ? 400 : 500}
                >
                  <LineChart
                    data={chartData}
                    margin={{
                      top: 5,
                      right: window.innerWidth < 768 ? 15 : 30,
                      left: window.innerWidth < 768 ? -10 : 10,
                      bottom: window.innerWidth < 768 ? 70 : 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="timestamp"
                      tick={{
                        fontSize: window.innerWidth < 768 ? 10 : 12,
                        angle: window.innerWidth < 768 ? -45 : 0,
                        textAnchor: window.innerWidth < 768 ? "end" : "middle",
                      }}
                      height={window.innerWidth < 768 ? 70 : 30}
                    />
                    <YAxis
                      tickFormatter={(value) =>
                        chartFilter === "averageLengthOfStay"
                          ? convertDaysToMonths(value).toFixed(1) + ""
                          : shouldDisplayPercentages()
                          ? formatPercentage(value)
                          : value
                      }
                      domain={
                        shouldDisplayPercentages() ? [0, 100] : [0, "auto"]
                      }
                      tick={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
                      width={window.innerWidth < 768 ? 40 : 60}
                    />
                    <Legend
                      wrapperStyle={{
                        paddingTop: window.innerWidth < 768 ? "8px" : "20px",
                        fontSize: window.innerWidth < 768 ? "12px" : "14px", // Increased from 10px/12px to 12px/14px
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "8px",
                        maxHeight: "80px",
                        overflowY: "auto",
                        padding: "0 8px",
                        marginBottom: "8px",
                      }}
                      layout="horizontal"
                      align="center"
                    />
                    <Tooltip
                      content={
                        <CustomTooltip
                          dataKeys={
                            chartFilter === "ageDemographics"
                              ? [
                                  { label: "18-25" },
                                  { label: "26-35" },
                                  { label: "36-45" },
                                  { label: "46-55" },
                                  { label: "56-65+" },
                                ]
                              : chartFilter === "soberDaysOnIntake"
                              ? [
                                  ...Object.keys(housesData).map(
                                    (houseName) => ({
                                      label: `${houseName} Sober Days`,
                                      dataKey: `${houseName} Sober Days`,
                                      formatter: formatStayDuration,
                                    })
                                  ),
                                  {
                                    label: "Total Sober Days",
                                    dataKey: "Total Sober Days",
                                    formatter: formatStayDuration,
                                  },
                                ]
                              : chartFilter === "averageLengthOfStay" ||
                                chartFilter === "sobrietyTime"
                              ? [
                                  ...Object.keys(housesData).map(
                                    (houseName) => ({
                                      label: `${houseName} ${
                                        chartFilter === "averageLengthOfStay"
                                          ? "Average Stay"
                                          : "Sobriety"
                                      }`,
                                      dataKey: `${houseName} ${
                                        chartFilter === "averageLengthOfStay"
                                          ? "Average Stay"
                                          : "Sobriety"
                                      }`,
                                      formatter:
                                        chartFilter === "averageLengthOfStay"
                                          ? formatStayDuration
                                          : formatSobriety,
                                    })
                                  ),
                                  {
                                    label:
                                      chartFilter === "averageLengthOfStay"
                                        ? "Total Average Stay"
                                        : "Total Sobriety",
                                    dataKey:
                                      chartFilter === "averageLengthOfStay"
                                        ? "Total Average Stay"
                                        : "Total Sobriety",
                                    formatter:
                                      chartFilter === "averageLengthOfStay"
                                        ? formatStayDuration
                                        : formatSobriety,
                                  },
                                ]
                              : chartFilter === "medications"
                              ? [
                                  { label: "No Meds", dataKey: "No Meds" },
                                  {
                                    label: "1 Medication",
                                    dataKey: "1 Medication",
                                  },
                                  {
                                    label: "2 Medications",
                                    dataKey: "2 Medications",
                                  },
                                  {
                                    label: "3 Medications",
                                    dataKey: "3 Medications",
                                  },
                                  {
                                    label: "4 Medications",
                                    dataKey: "4 Medications",
                                  },
                                  {
                                    label: "5 Medications",
                                    dataKey: "5 Medications",
                                  },
                                ]
                              : chartFilter === "recidivismData"
                              ? [
                                  {
                                    label: "All Alumni Recidivism",
                                    dataKey: "All Alumni Recidivism",
                                  },
                                  {
                                    label: "Probation/Parole Recidivism",
                                    dataKey: "Probation/Parole Recidivism",
                                  },
                                ]
                              : chartFilter === "employmentData"
                              ? [
                                  {
                                    label: "Employed at Intake",
                                    dataKey: "Employed at Intake",
                                  },
                                  {
                                    label: "Employed at Discharge",
                                    dataKey: "Employed at Discharge",
                                  },
                                ]
                              : chartFilter === "probationChart"
                              ? [
                                  {
                                    label: "On Probation/Parole",
                                    dataKey: "On Probation/Parole",
                                  },
                                  {
                                    label: "Not On Probation/Parole",
                                    dataKey: "Not On Probation/Parole",
                                  },
                                ]
                              : chartFilter === "dischargeStatus"
                              ? [
                                  {
                                    label: "Healthy Transition",
                                    dataKey: "Healthy Transition",
                                  },
                                  { label: "Removed", dataKey: "Removed" },
                                  {
                                    label: "Against Advice",
                                    dataKey: "Against Advice",
                                  },
                                ]
                              : chartFilter === "matData"
                              ? [
                                  { label: "On MAT", dataKey: "On MAT" },
                                  {
                                    label: "Not On MAT",
                                    dataKey: "Not On MAT",
                                  },
                                ]
                              : chartFilter === "percentageFull"
                              ? [{ label: "%", dataKey: "percentageFull" }]
                              : []
                          }
                          formatSobriety={formatSobriety}
                          formatStayDuration={formatStayDuration}
                        />
                      }
                    />
                    {chartFilter === "ageDemographics" && (
                      <>
                        <Line
                          type="monotone"
                          dataKey="18-25"
                          stroke="#8884d8"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="26-35"
                          stroke="#82ca9d"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="36-45"
                          stroke="#ffc658"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="46-55"
                          stroke="#f58231"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="56-65+"
                          stroke="#A020F0"
                          strokeWidth={3}
                          dot={false}
                        />
                      </>
                    )}
                    {chartFilter === "recidivismData" && (
                      <>
                        <Line
                          type="monotone"
                          dataKey="All Alumni Recidivism"
                          stroke="#FB7185"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="Probation/Parole Recidivism"
                          stroke="#F59E0B"
                          strokeWidth={3}
                          dot={false}
                        />
                      </>
                    )}
                    {chartFilter === "soberDaysOnIntake" && (
                      <>
                        {Object.keys(housesData).map((houseName, index) => (
                          <Line
                            key={houseName}
                            type="linear"
                            dataKey={`${houseName} Sober Days`}
                            stroke={getDynamicColor(index)}
                            strokeWidth={3}
                            dot={false}
                            connectNulls={true}
                          />
                        ))}
                        <Line
                          type="linear"
                          dataKey="Total Sober Days"
                          stroke="#C13011"
                          strokeWidth={3}
                          dot={false}
                          connectNulls={true}
                        />
                      </>
                    )}
                    {chartFilter === "sobrietyTime" && (
                      <>
                        {Object.keys(housesData).map((houseName, index) => (
                          <Line
                            key={houseName}
                            type="linear"
                            dataKey={`${houseName} Sobriety`}
                            stroke={getDynamicColor(index)}
                            strokeWidth={3}
                            dot={false}
                            connectNulls={true}
                          />
                        ))}
                        <Line
                          type="linear"
                          dataKey="Total Sobriety"
                          stroke="#C13011"
                          strokeWidth={3}
                          dot={false}
                          connectNulls={true}
                        />
                      </>
                    )}
                    {chartFilter === "medications" && (
                      <>
                        <Line
                          type="monotone"
                          dataKey="No Meds"
                          stroke="#8884d8"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="1 Medication"
                          stroke="#82ca9d"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="2 Medications"
                          stroke="#ffc658"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="3 Medications"
                          stroke="#f58231"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="4 Medications"
                          stroke="#A020F0"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="5 Medications"
                          stroke="#F8F2F1"
                          strokeWidth={3}
                          dot={false}
                        />
                      </>
                    )}
                    {chartFilter === "employmentData" && (
                      <>
                        <Line
                          type="monotone"
                          dataKey="Employed at Intake"
                          stroke="#8884d8"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="Employed at Discharge"
                          stroke="#82ca9d"
                          strokeWidth={3}
                          dot={false}
                        />
                      </>
                    )}
                    {chartFilter === "averageLengthOfStay" && (
                      <>
                        {Object.keys(housesData).map((houseName, index) => (
                          <Line
                            key={houseName}
                            type="linear"
                            dataKey={`${houseName} Average Stay`}
                            stroke={getDynamicColor(index)}
                            strokeWidth={3}
                            dot={false}
                            connectNulls={true}
                          />
                        ))}
                        <Line
                          type="linear"
                          dataKey="Total Average Stay"
                          stroke="#8884d8"
                          strokeWidth={3}
                          dot={false}
                          connectNulls={true}
                        />
                      </>
                    )}
                    {chartFilter === "probationChart" && (
                      <>
                        <Line
                          type="monotone"
                          dataKey="On Probation/Parole"
                          stroke="#8884d8"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="Not On Probation/Parole"
                          stroke="#82ca9d"
                          strokeWidth={3}
                          dot={false}
                        />
                      </>
                    )}
                    {chartFilter === "dischargeStatus" && (
                      <>
                        <Line
                          type="monotone"
                          dataKey="Healthy Transition"
                          stroke="#8884d8"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="Removed"
                          stroke="#82ca9d"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="Against Advice"
                          stroke="#ffc658"
                          strokeWidth={3}
                          dot={false}
                        />
                      </>
                    )}
                    {chartFilter === "percentageFull" && (
                      <>
                        <Line
                          type="monotone"
                          dataKey="percentageFull"
                          stroke="#8884d8"
                          strokeWidth={3}
                          dot={false}
                        />
                      </>
                    )}
                    {chartFilter === "matData" && (
                      <>
                        <Line
                          type="monotone"
                          dataKey="On MAT"
                          stroke="#8884d8"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="Not On MAT"
                          stroke="#82ca9d"
                          strokeWidth={3}
                          dot={false}
                        />
                      </>
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DataTracker;
